'use client';
import './styles/@theme';

import type { NavigationItemUI } from '@/logic/page-config/navigation';
import Link from 'next/link';
import { GameUrl } from '@/app/components/game/subcomponents/game-url/game-url';
import { classes } from '@/utils/classes/classes';
import { usePathname } from 'next/navigation';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { NotificationIcon } from '@/app/elements/notification-icon/notification-icon';
import { useShouldShowNotification } from '@/hooks/use-show-notification-icon';
import { getNavigatedToProps } from '@/tracking/amplitude/events/general/navigation-link-clicked';
import { useStore } from '@/store/store';
import { useEffect, useState } from 'react';
import { useGlobalContext } from '@/store/contexts/global-context';
import { shouldDisplayNewTag } from '@/app/elements/nav-item/utils/rewards-badge';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { RewardsHubFeatures } from '@fanduel/rewards-hub-client';
import type { GameLaunchPayload } from '@/logic/game-info/game-info';
import {
    buildBottomNavGameSelectedProps,
    MOBILE_BOTTOM_NAV_MODULE
} from '@/tracking/amplitude/events/general/bottom-nav-game-selected';

type BottomNavItemProps = NavigationItemUI & {
    gameLaunchData?: GameLaunchPayload;
};

export const BottomNavItem: React.FC<BottomNavItemProps> = ({
    name,
    pathname,
    customImage,
    newItemsCount,
    linkType,
    type,
    gameLaunchData
}) => {
    const path = usePathname();
    const shouldShowNotificationIcon = useShouldShowNotification(Boolean(newItemsCount));
    const { sendEvent } = useStore((state) => state.tracking);
    const { handleShowLoading } = useStore((s) => s.loading);
    const { isLoggedIn, featureFlags } = useGlobalContext();
    const userId = getCookieValue(COOKIES.USER_ID);

    const [shouldShowRewardsNotificationIcon, setShouldShowRewardsNotificationIcon] = useState(false);

    useEffect(() => {
        if (!!type && ['rewards', 'promotions'].includes(type.toLowerCase())) {
            setShouldShowRewardsNotificationIcon(
                shouldDisplayNewTag(isLoggedIn, !!featureFlags[RewardsHubFeatures.HAS_REWARDS_WIDGET], userId)
            );
        }
    }, [type, isLoggedIn, featureFlags, path]);

    const isGameLink = linkType === 'game' || linkType === 'game_lobby';

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.stopPropagation();

        if (pathname !== path) {
            handleShowLoading();
        }

        const eventProps = isGameLink
            ? buildBottomNavGameSelectedProps(gameLaunchData)
            : getNavigatedToProps({
                  module: 'bottom_ribbon',
                  linkText: name,
                  linkUrl: pathname!
              });

        sendEvent([{ destination: 'amplitude', eventProps }]);
    };

    const navItemClassName = classes('bottom-nav-item', pathname === path && 'active');

    const inner = (
        <>
            {shouldShowRewardsNotificationIcon && <NotificationIcon count={1} />}
            {shouldShowNotificationIcon && <NotificationIcon count={newItemsCount} />}
            {customImage.url && <img src={customImage.url} alt={customImage.name} width={20} height={20} />}
            <p>{name}</p>
        </>
    );

    if (isGameLink && gameLaunchData?.launcherUrlProps) {
        return (
            <GameUrl
                className={navItemClassName}
                launcherUrlProps={gameLaunchData.launcherUrlProps}
                gameUid={gameLaunchData.gameUid}
                gameName={gameLaunchData.gameName}
                module={MOBILE_BOTTOM_NAV_MODULE}
                amplitudeEventProps={() => buildBottomNavGameSelectedProps(gameLaunchData)}>
                {inner}
            </GameUrl>
        );
    }

    return (
        <Link className={navItemClassName} prefetch={false} href={pathname!} onClick={handleClick}>
            {inner}
        </Link>
    );
};
